/**
 * PartialPaymentDialog — внесение оплаты по траншу (полная/частичная).
 * Показывает план/оплачено/остаток; сумма (MoneyInput) + дата (DatePicker) + комментарий.
 * POST /payments/:id/transactions со стабильным Idempotency-Key на сессию диалога.
 *
 * Валидация суммы: 0 < amount ≤ остаток. Подсказка о результирующем статусе (Оплачен/Частично).
 */

import { useMemo, useRef, useState } from 'react';
import type { PaymentDto } from '../../../lib/types';
import { formatRub, minorToRubString } from '../../../lib/money';
import { fmtDate, todayYmd } from '../lib/dates';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { MoneyInput } from '../MoneyInput';
import { DatePicker } from '../ui/DatePicker';
import { useAddTransaction, type DealScope } from '../api';
import { ApiError } from '../../../lib/apiClient';

export interface PartialPaymentDialogProps {
  scope: DealScope;
  payment: PaymentDto;
  onClose: () => void;
}

/** Простой генератор ключа идемпотентности (UUID при поддержке crypto, иначе псевдо). */
function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pw-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Диагностический маячок на сервер (<img>, читаем в логах Railway; без консоли браузера). */
function payBeacon(ev: string, extra: Record<string, string | number> = {}): void {
  try {
    const q = new URLSearchParams({ ev, ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, String(v)])) });
    const img = new Image();
    img.src = `/api/health/diag?${q.toString()}`;
  } catch {
    /* ignore */
  }
}

export function PartialPaymentDialog({ scope, payment, onClose }: PartialPaymentDialogProps): JSX.Element {
  const remainderMinor = payment.remainderMinor;
  const [amountMinor, setAmountMinor] = useState<string>(remainderMinor);
  const [date, setDate] = useState<string>(todayYmd());
  const [dateOpen, setDateOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(makeIdempotencyKey()).current;

  const addTx = useAddTransaction(scope);

  const remainder = useMemo(() => BigInt(remainderMinor), [remainderMinor]);
  const amount = useMemo(() => {
    try {
      return BigInt(amountMinor);
    } catch {
      return 0n;
    }
  }, [amountMinor]);

  const alreadyPaid = remainder <= 0n;
  const invalid = alreadyPaid || amount <= 0n || amount > remainder;
  const isFull = amount === remainder && remainder > 0n;

  const submit = (): void => {
    setError(null);
    payBeacon('pay_submit', { amt: amountMinor, rem: remainderMinor });
    addTx.mutate(
      {
        paymentId: payment.id,
        idempotencyKey,
        body: {
          amountMinor,
          actualDate: date,
          paidAt: new Date(`${date}T12:00:00`).toISOString(),
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          payBeacon('pay_ok', { amt: amountMinor });
          onClose();
        },
        onError: (e) => {
          const status = e instanceof ApiError ? e.status : 0;
          const msg = e instanceof Error ? e.message : String(e);
          payBeacon('pay_err', { status, msg: msg.slice(0, 90) });
          if (e instanceof ApiError && e.isVersionConflict) {
            setError('Транш изменён в другом месте. Закройте окно и попробуйте снова.');
          } else if (e instanceof ApiError) {
            setError(e.message || 'Не удалось провести оплату.');
          } else {
            setError('Не удалось провести оплату.');
          }
        },
      },
    );
  };

  return (
    <Dialog
      title={`Оплата транша №${payment.number}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={addTx.isPending}>
            Отмена
          </Button>
          <Button variant="primary" onClick={submit} disabled={invalid || addTx.isPending}>
            {addTx.isPending ? 'Проведение…' : 'Провести оплату'}
          </Button>
        </>
      }
    >
      <dl className="pw-summary">
        <div className="pw-summary__row">
          <dt>К оплате по плану</dt>
          <dd className="pw-num">{formatRub(payment.plannedAmountMinor)}</dd>
        </div>
        <div className="pw-summary__row">
          <dt>Уже оплачено</dt>
          <dd className="pw-num">{formatRub(payment.paidAmountMinor)}</dd>
        </div>
        <div className="pw-summary__row pw-summary__row--accent">
          <dt>Остаток по траншу</dt>
          <dd className="pw-num">{formatRub(remainderMinor)}</dd>
        </div>
      </dl>

      <label className="pw-field">
        <span className="pw-field__label">Сумма поступления</span>
        <MoneyInput
          initialMinor={amountMinor}
          onCommit={setAmountMinor}
          ariaLabel="Сумма поступления"
          className="pw-field__control"
        />
        {invalid ? (
          <span className="pw-field__err" role="alert">
            Сумма должна быть от 0,01&nbsp;₽ до {minorToRubString(remainderMinor)}&nbsp;₽
          </span>
        ) : null}
      </label>

      <div className="pw-field">
        <span className="pw-field__label">Дата поступления</span>
        <DatePicker
          value={date}
          open={dateOpen}
          onOpenChange={setDateOpen}
          onSelect={(ymd) => {
            if (ymd) setDate(ymd);
          }}
          trigger={
            <button type="button" className="pw-input pw-input--date" aria-label="Дата поступления">
              {fmtDate(date)}
            </button>
          }
        />
      </div>

      <label className="pw-field">
        <span className="pw-field__label">Комментарий</span>
        <textarea
          className="pw-textarea"
          rows={2}
          value={comment}
          maxLength={500}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Необязательно"
        />
      </label>

      <p className="pw-hint">
        {alreadyPaid
          ? 'Транш уже полностью оплачен — вносить нечего.'
          : invalid
            ? 'Укажите сумму в пределах остатка.'
            : isFull
              ? 'Транш будет помечен как «Оплачен».'
              : 'Транш перейдёт в статус «Частично оплачен».'}
      </p>

      {error ? (
        <p className="pw-field__err" role="alert">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
